import { Link } from 'react-router';
import { ArrowRight, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useSetupStatus } from '@/lib/setup-status';

/**
 * The dashboard's follow-up on unfinished first-time setup (the 2026-09-25
 * design).
 *
 * Not dismissible, and it needs no dismiss: it self-clears. The only way to
 * remove it is to finish, and finishing is three fields. A dismiss button
 * would let a planner hide the reason their outbound mail is unsigned or
 * unsendable, which is the one thing this card exists to prevent.
 *
 * It reads `complete` off the server rather than recomputing it, so it cannot
 * disagree with the page it links to. And it lists only the three required
 * steps — the rolodex is optional, and an optional row in a card whose whole
 * purpose is to be finishable muddies what is being asked.
 */
export default function SetupCard() {
  const { data: status } = useSetupStatus();

  if (!status || status.complete) return null;

  // The two steps a planner cannot send without. The signature and the
  // rolodex are optional on /welcome and are not chased from here.
  const steps = [
    { label: 'Connect Gmail', done: status.gmailConnected },
    { label: 'Your name and business', done: Boolean(status.businessName) },
  ];
  const done = steps.filter((s) => s.done).length;

  return (
    <Card className="rounded-3xl bg-card shadow-ambient">
      <CardContent className="flex flex-wrap items-center justify-between gap-6 py-5">
        <div className="min-w-0 space-y-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="display-tight text-lg font-semibold">Finish setup</h2>
            <p className="label-caps text-xs text-muted-foreground">{done} of 2 done</p>
          </div>

          <ul className="flex flex-wrap gap-x-5 gap-y-2">
            {steps.map((step) => (
              <li key={step.label} className="flex items-center gap-2 text-sm">
                <span
                  aria-hidden
                  className={[
                    'grid size-4 shrink-0 place-items-center rounded-full border',
                    step.done
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border bg-[var(--color-surface-container-high)]',
                  ].join(' ')}
                >
                  {step.done && <Check className="size-2.5" strokeWidth={3} />}
                </span>
                <span className={step.done ? 'text-muted-foreground line-through' : ''}>
                  {step.label}
                </span>
                <span className="sr-only">{step.done ? 'done' : 'not done yet'}</span>
              </li>
            ))}
          </ul>

          {!status.gmailConnected && (
            <p className="text-xs text-muted-foreground">
              Inquiries can't be sent until Gmail is connected.
            </p>
          )}
        </div>

        <Link
          to="/welcome"
          className="inline-flex items-center gap-2 text-sm font-medium text-primary underline-offset-4 hover:underline"
        >
          Continue setup
          <ArrowRight className="size-4" />
        </Link>
      </CardContent>
    </Card>
  );
}
